import type { HTMLAttributes, ReactNode } from "react";

import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import clsx from "clsx";
import Link from "next/link";
import { usePathname } from "next/navigation";

import type { NavigationItem } from "@/src/components/navigation/NavigationItems";

import { robotoCondensed } from "@/fonts";
import { isExternal } from "@/src/app/externalLinks";

export interface NavigationItemsConsumerProps
  extends HTMLAttributes<HTMLDivElement> {
  readonly items: NavigationItem[];
}

export function NavigationItemsConsumer({
  className,
  items,
}: NavigationItemsConsumerProps): ReactNode {
  const pathname = usePathname();
  return (
    <NavigationMenu.List asChild>
      <ol
        aria-label="Menu principal"
        className={clsx(
          robotoCondensed.className,
          "font-bold-antialiased bg-primary flex w-full flex-col gap-2 text-lg uppercase tracking-wide text-white",
          "md:w-auto md:max-w-full md:flex-row",
          "lg:text-xl",
          className,
        )}
      >
        {items.map(({ href, label }) => (
          <NavigationMenu.Item
            className={clsx(
              "flex items-center justify-center",
              "md:before:inline-block md:before:h-5 md:before:w-px [&:not(:first-child):before]:ml-2 md:[&:not(:first-child):before]:mr-4 md:[&:not(:first-child):before]:bg-white",
            )}
            key={label}
          >
            <NavigationMenu.Link
              asChild
              className={clsx(
                "w-full rounded-sm px-3 py-1.5 text-center transition-all",
                "hover:bg-primary-hover",
                { "bg-primary-focus": pathname === href },
              )}
            >
              {isExternal(href) ? (
                <a href={href}>{label}</a>
              ) : (
                <Link href={href}>{label}</Link>
              )}
            </NavigationMenu.Link>
          </NavigationMenu.Item>
        ))}
      </ol>
    </NavigationMenu.List>
  );
}
